import { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useAuth } from "@/contexts/AuthContext";
import { RouteResetWrapper } from "@/components/ErrorBoundary";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import DashboardLayout from "@/layouts/DashboardLayout";
import MarketingLayout from "@/layouts/MarketingLayout";
import OverviewPage from "@/pages/OverviewPage";
import ApiKeysPage from "@/pages/ApiKeysPage";
import UsagePage from "@/pages/UsagePage";
import BillingPage from "@/pages/BillingPage";
import BillingHistoryPage from "@/pages/BillingHistoryPage";
import SettingsPage from "@/pages/SettingsPage";
import WebhooksPage from "@/pages/WebhooksPage";
import NotFoundPage from "@/pages/NotFoundPage";

const LandingPage = lazy(() => import("@/pages/marketing/LandingPage"));
const FeaturesPage = lazy(() => import("@/pages/marketing/FeaturesPage"));
const PricingPage = lazy(() => import("@/pages/marketing/PricingPage"));
const BlogPage = lazy(() => import("@/pages/marketing/BlogPage"));
const DocsPage = lazy(() => import("@/pages/marketing/DocsPage"));
const PlaygroundPage = lazy(() => import("@/pages/marketing/PlaygroundPage"));
const StatusPage = lazy(() => import("@/pages/marketing/StatusPage"));
const AboutPage = lazy(() => import("@/pages/marketing/AboutPage"));
const TermsPage = lazy(() => import("@/pages/marketing/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/marketing/PrivacyPage"));

function LazyFallback() {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
      }}
    >
      <CircularProgress />
    </Box>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <RouteResetWrapper>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/verify/:status" element={<VerifyEmailPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route
            index
            element={<Navigate to="/dashboard/overview" replace />}
          />
          <Route path="overview" element={<OverviewPage />} />
          <Route path="api-keys" element={<ApiKeysPage />} />
          <Route path="usage" element={<UsagePage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="billing/history" element={<BillingHistoryPage />} />
          <Route
            path="logs"
            element={<Navigate to="/dashboard/usage" replace />}
          />
          <Route path="webhooks" element={<WebhooksPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Legacy redirects */}
        <Route
          path="/overview"
          element={<Navigate to="/dashboard/overview" replace />}
        />
        <Route
          path="/api-keys"
          element={<Navigate to="/dashboard/api-keys" replace />}
        />
        <Route
          path="/usage"
          element={<Navigate to="/dashboard/usage" replace />}
        />
        <Route
          path="/billing"
          element={<Navigate to="/dashboard/billing" replace />}
        />
        {/* Post-checkout return target — destination URL set by checkoutClient.upsertTenant in dtsaaslabs-dev (PDF-16). The "?subscribed=1" suffix is intentional and distinct from the Payload-default "?subscribed=true" so the dashboard's banner read remains unambiguous. */}
        <Route
          path="/billing/return"
          element={<Navigate to="/dashboard/billing?subscribed=1" replace />}
        />

        <Route path="/" element={<MarketingLayout />}>
          <Route
            index
            element={
              <Suspense fallback={<LazyFallback />}>
                <LandingPage />
              </Suspense>
            }
          />
          <Route
            path="features"
            element={
              <Suspense fallback={<LazyFallback />}>
                <FeaturesPage />
              </Suspense>
            }
          />
          <Route
            path="pricing"
            element={
              <Suspense fallback={<LazyFallback />}>
                <PricingPage />
              </Suspense>
            }
          />
          <Route
            path="blog"
            element={
              <Suspense fallback={<LazyFallback />}>
                <BlogPage />
              </Suspense>
            }
          />
          <Route
            path="docs"
            element={
              <Suspense fallback={<LazyFallback />}>
                <DocsPage />
              </Suspense>
            }
          />
          <Route
            path="playground"
            element={
              <Suspense fallback={<LazyFallback />}>
                <PlaygroundPage />
              </Suspense>
            }
          />
          <Route
            path="status"
            element={
              <Suspense fallback={<LazyFallback />}>
                <StatusPage />
              </Suspense>
            }
          />
          <Route
            path="about"
            element={
              <Suspense fallback={<LazyFallback />}>
                <AboutPage />
              </Suspense>
            }
          />
          <Route
            path="terms"
            element={
              <Suspense fallback={<LazyFallback />}>
                <TermsPage />
              </Suspense>
            }
          />
          <Route
            path="privacy"
            element={
              <Suspense fallback={<LazyFallback />}>
                <PrivacyPage />
              </Suspense>
            }
          />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </RouteResetWrapper>
  );
}
